import React, { useEffect, useRef, useState } from "react";
import { OverallSticker } from "./OverallSticker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toPng, toBlob } from "html-to-image";
import { Download, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Dialog que abre a figurinha Overall do aluno. Carrega evaluations automaticamente.
 * Usage:
 *   <OverallStickerDialog student={s} />              (controlado por trigger interno)
 *   <OverallStickerDialog student={s} evaluations={list} />  (passe lista pronta)
 *   <OverallStickerButton student={s} />              (botão padrão)
 */
export function OverallStickerDialog({ student, evaluations: evalsProp, trigger, open: openProp, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const ctrl = openProp !== undefined;
  const isOpen = ctrl ? openProp : open;
  const setIsOpen = ctrl ? onOpenChange : setOpen;
  const [evaluations, setEvaluations] = useState(evalsProp || []);
  const stickerRef = useRef(null);

  useEffect(() => {
    if (!isOpen || evalsProp || !student?.id) return;
    api.get(`/evaluations/student/${student.id}`)
      .then(r => setEvaluations(r.data))
      .catch(err => toast.error(formatApiError(err)));
  }, [isOpen, evalsProp, student?.id]);

  const download = async () => {
    if (!stickerRef.current) return;
    try {
      const dataUrl = await toPng(stickerRef.current, { cacheBust: true, pixelRatio: 2 });
      const link = document.createElement("a");
      link.download = `overall-${(student?.name || "aluno").replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Figurinha baixada");
    } catch (err) {
      toast.error("Falha ao gerar imagem");
    }
  };

  const share = async () => {
    if (!stickerRef.current) return;
    try {
      const blob = await toBlob(stickerRef.current, { cacheBust: true, pixelRatio: 2 });
      if (!blob) throw new Error("blob null");
      const file = new File([blob], `overall-${student?.name || "aluno"}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Overall de ${student?.name}`,
          text: `Vôlei Vida — ${student?.name}`,
        });
      } else if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        toast.success("Imagem copiada!");
      } else {
        await download();
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Compartilhamento não suportado");
    }
  };

  return (
    <>
      {trigger && <span onClick={() => setIsOpen(true)}>{trigger}</span>}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="overall-sticker-dialog">
          <DialogHeader>
            <DialogTitle>Figurinha Overall</DialogTitle>
            <DialogDescription>
              Baseada na avaliação mais recente. Compartilhe ou baixe como PNG.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <OverallSticker ref={stickerRef} student={student} evaluations={evaluations} />
          </div>
          <div className="flex flex-wrap justify-center gap-2 pt-2 border-t border-border">
            <Button onClick={download} data-testid="overall-download-button" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold hover:bg-[hsl(var(--vv-lime)/0.85)]">
              <Download className="size-4 mr-2" /> Baixar PNG
            </Button>
            <Button onClick={share} variant="outline" data-testid="overall-share-button">
              <Share2 className="size-4 mr-2" /> Compartilhar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function OverallStickerButton(props) {
  return (
    <OverallStickerDialog
      {...props}
      trigger={
        <Button variant="outline" className="border-[hsl(var(--vv-yellow))/0.6] text-[hsl(var(--vv-yellow))] hover:bg-[hsl(var(--vv-yellow))] hover:text-[hsl(218_50%_10%)] font-bold uppercase tracking-wider" data-testid="open-overall-button">
          <Sparkles className="size-4 mr-2" /> Overall
        </Button>
      }
    />
  );
}
